import { useMemo } from 'react';
import { EventWithMarketsAndSelections, MarketWithSelections } from '../sportsbook-types';

const MAX_SELECTIONS_TO_DISPLAY = 3;

type MarketSelector = (event: EventWithMarketsAndSelections) => MarketWithSelections | undefined;

const createEventIdToSelectedMarketMap = (events: EventWithMarketsAndSelections[], selector: MarketSelector) => {
	const eventIdToMarket = events.reduce<Record<string, MarketWithSelections | undefined>>((map, event) => {
		const market = selector(event);

		if (market) {
			// eslint-disable-next-line no-param-reassign
			map[event.id] = market;
		}

		return map;
	}, {});

	return {
		eventIdToMarket
	};
};

/**
 * Resolves events to a selected market.
 *
 * You can control which market type IDs to use as common market by passing marketTypeIds.
 * Otherwise, it will resolve to any first applicable market for each event independently.
 *
 * @param {EventWithMarketsAndSelections[]} events - List of events.
 * @param {string[]} marketTypeIds - preferred market type ids.
 * @returns {Record<string, MarketWithSelections | undefined>} - Event ID to market mapping.
 */
const useEventsToMarketResolver = (events: EventWithMarketsAndSelections[], marketTypeIds?: string[]) => {
	return useMemo(() => {
		if (marketTypeIds && marketTypeIds.length > 0) {
			return createEventIdToSelectedMarketMap(events, event =>
				marketTypeIds.reduce<MarketWithSelections | undefined>((market, marketTypeId) => {
					// Market already found, return it.
					if (market) {
						return market;
					}

					return event.markets.find(m => m.marketType.id === marketTypeId);
				}, undefined)
			);
		}

		return createEventIdToSelectedMarketMap(events, event =>
			event.markets.find(
				market =>
					market.selections.length > 0 &&
					market.marketType.selectionsTemplate.length <= MAX_SELECTIONS_TO_DISPLAY
			)
		);
	}, [events, marketTypeIds]);
};

export default useEventsToMarketResolver;
